import { GridAlignment, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { FormikValues } from 'formik';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActions } from '../../../hooks/useActions';
import { useStore } from '../../../hooks/useStore';
import { ThreePointMenu } from '../../../lib/components';
import { TableDataGrid, TableDataGridProps } from '../../../lib/components/compound/tableDataGrid';
import { DeleteIconSvg } from '../../../lib/icons';
import Util from '../../../misc/util';
import { useStepper } from '../partials/actionStepperPartial';

export const EmployeeUpdateTable: FC<{ values: FormikValues | undefined }> = observer(
  ({ values }) => {
    const { employeeStore, companyStore } = useStore();
    const { selectedUsers, setSelectedUsers } = useActions();
    const { t } = useTranslation();
    const { activeStep } = useStepper();
    const [rows, setRows] = useState<TableDataGridProps['rows']>();

    const renderThreePointCell = (params: GridRenderCellParams) => (
      <ThreePointMenu
        config={[
          {
            label: t('common:REMOVE'),
            icon: <DeleteIconSvg />,
            isWarning: true,
            onClick: () => setSelectedUsers((prevIds) => prevIds?.filter((id) => id !== params.id))
          }
        ]}
        item="remove employee"
      />
    );

    const getRows = useCallback(async () => {
      const employees = await employeeStore.fetchList([
        'cost_center',
        'department',
        'first_name',
        'id',
        'last_name',
        'location'
      ]);
      setRows(
        employees
          ?.filter((employee) => employee.id && selectedUsers?.includes(employee.id))
          .map((employee) => ({
            id: employee.id,
            employee: `${employee.first_name} ${employee.last_name}`,
            location: Util.getValueFromStore(employee.location as string, 'location', companyStore),
            new_location: Util.getValueFromStore(
              values?.location as string,
              'location',
              companyStore
            ),
            department: Util.getValueFromStore(
              employee.department as string,
              'department',
              companyStore
            ),
            new_department: Util.getValueFromStore(values?.department, 'department', companyStore),
            cost_center: Util.getValueFromStore(
              employee.cost_center as string,
              'cost_center',
              companyStore
            ),
            new_cost_center: Util.getValueFromStore(
              values?.cost_center,
              'cost_center',
              companyStore
            ),
            note: values?.note
          }))
      );
    }, [values, selectedUsers, employeeStore, companyStore]);

    const columns = useMemo(
      () => [
        {
          field: 'employee',
          headerName: t('employee:EMPLOYEES'),
          flex: 1,
          cellClassName: () => (activeStep === 1 ? 'pxtra-bold' : 'pxtra')
        },
        {
          field: 'location',
          headerName: t('employee:WORK_LOCATION'),
          hide: values?.field_to_update !== 'location',
          flex: 1
        },
        {
          field: 'new_location',
          headerName: t('employee:WORK_LOCATION', { context: 'new' }),
          hide: activeStep === 0 || values?.field_to_update !== 'location',
          flex: 1
        },
        {
          field: 'department',
          headerName: t('employee:DEPARTMENT'),
          hide: values?.field_to_update !== 'department',
          flex: 1
        },
        {
          field: 'new_department',
          headerName: t('employee:DEPARTMENT', { context: 'new' }),
          hide: activeStep === 0 || values?.field_to_update !== 'department',
          flex: 1
        },
        {
          field: 'cost_center',
          headerName: t('employee:COST_CENTER'),
          hide: values?.field_to_update !== 'cost_center',
          flex: 1
        },
        {
          field: 'new_cost_center',
          headerName: t('employee:COST_CENTER', { context: 'new' }),
          hide: activeStep === 0 || values?.field_to_update !== 'cost_center',
          flex: 1
        },
        {
          field: 'note',
          headerName: t('common:NOTE'),
          hide: activeStep === 0 || values?.field_to_update !== 'note',
          flex: 1
        },
        {
          field: 'menu',
          headerName: '',
          align: 'right' as GridAlignment,
          width: 10,
          renderCell: renderThreePointCell
        }
      ],
      [activeStep]
    );

    useEffect(() => {
      getRows();
    }, [getRows]);

    return (
      <TableDataGrid
        rows={rows || []}
        columns={columns}
        checkboxSelection={false}
        components={{ Toolbar: null }}
        pagination
      />
    );
  }
);
